import { intersection } from "lodash";
import { useMemo } from "react";

import { availableMeshFormats } from "@/utils/meshFormats";

import { DriveFileItem, ExportOptionId, SimpleFile } from "../types";
import { isMesh, isRasterImage, isVideo } from "../utils";

const getFileExportOptions = (file: SimpleFile): ExportOptionId[] => {
  const optionIds: ExportOptionId[] = ["SAVE_TO_DRIVE", "DOWNLOAD_FILES"];

  if (isRasterImage(file)) {
    optionIds.push("DOWNLOAD_AS");
  }

  if (isVideo(file)) {
    optionIds.push("DOWNLOAD_AS");
  }

  if (isMesh(file)) {
    // OBJ is always offered: files stored before the model's own OBJ export was kept
    // fall back to converting the GLB in the browser. FBX has no such fallback, so it
    // is offered only when the generating model actually exported one.
    optionIds.push("DOWNLOAD_AS_OBJ");
    if (availableMeshFormats(file).includes("fbx")) {
      optionIds.push("DOWNLOAD_AS_FBX");
    }
  }

  return optionIds;
};

const getAssetExportOptions = (): ExportOptionId[] => {
  return ["SAVE_TO_DRIVE", "DOWNLOAD_FILES"];
};

interface UseAvailableExportOptionIdsProps {
  items: DriveFileItem[];
}

export const useAvailableExportOptionIds = (
  props: UseAvailableExportOptionIdsProps,
) => {
  const { items } = props;

  return useMemo(() => {
    // To evaluate the available export options, we get all the possible options for each selected
    // item, then we take the intersection of all possible options. This avoids us having to
    // maintain a complex matrix of possibilities across selections.
    const availableOptionIds = items.reduce<ExportOptionId[]>(
      (possibleActions, item, i) => {
        let itemActions: ExportOptionId[] = [];

        switch (item?.__typename) {
          case "File":
            itemActions = getFileExportOptions(item as SimpleFile);
            break;
          case "Asset":
            itemActions = getAssetExportOptions();
            break;
        }

        if (i === 0) {
          return itemActions;
        }

        return intersection(possibleActions, itemActions);
      },
      [],
    );

    return new Set(availableOptionIds);
  }, [items]);
};
